import { useContext, useState, useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm, useFormState } from 'react-hook-form';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
  SidebarWrapper,
} from '@components';
import { authDetailsQuery, userMutation, userQuery } from '@queries';
import { ButtonSpinnerIcon, ChevronRightIcon } from '@svgs';
import { GlobalContext } from '@contexts';
import { EmailField, FormFieldset, TextField } from '@Forms/fields.jsx';
import { errorsFromMutate } from '@Forms/utils.js';

import AccountSidebar from '../components/AccountSidebar/AccountSidebar.jsx';

export const YourAccount = () => {
  const { t, i18n } = useTranslation();
  const globals = useContext(GlobalContext);

  // get auth details
  const { data: authData, isPending: authIsPending } =
    useQuery(authDetailsQuery());

  // get user
  const {
    data: userData,
    isPending: userIsPending,
    isError,
  } = useQuery(userQuery(authData?.user.id));

  // save user
  const mutation = useMutation(userMutation(authData?.user.id));

  return (
    <>
      <Header />
      <BodyWrapper>
        <AccountSidebar />
        <MainWrapper>
          <div className="sb-main-breadcrumbs">
            <ul className="sb-flex">
              <li className="sb-flex">
                <span>Account settings</span>
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                <span>Your account</span>
              </li>
            </ul>
          </div>
          <div className="sb-manage-content">
            <h1>{t('Your account')}</h1>
            {globals.user.is_staff && (
              <p>Account settings for {globals.user.email} can't be edited.</p>
            )}
            {!globals.user.is_staff && (authIsPending || userIsPending) && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {!globals.user.is_staff && !userIsPending && userData && (
              <UserForm userData={userData} mutation={mutation} />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};

const UserForm = ({ mutation, userData }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getFieldState,
    setError,
    control,
    resetField,
    trigger,
    watch,
  } = useForm({
    defaultValues: userData,
    mode: 'onBlur',
  });
  const { isValid } = useFormState({ control });

  const onSubmit = (data) => {
    if (!data.new_password) {
      // remove password from bundle unless it is changing
      delete data.new_password;
      delete data.confirm_password;
    }
    mutation.mutate(data, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        errorsList.forEach(({ field, error }) => setError(field, error));
      },
      onSuccess: () => {
        if (data.new_password) {
          // reset the password fields
          resetField('new_password');
          resetField('confirm_password');
        }
      },
    });
  };

  const passwordsMatch = (value, fields) => {
    if (
      watch('new_password') &&
      watch('new_password').length > 7 &&
      value != watch('new_password')
    ) {
      return 'Passwords do not match';
    }
    return true;
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` +
        (mutation.isPending ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <legend>Your profile</legend>
        <TextField
          register={register}
          options={{ required: 'This field is required' }}
          error={errors.first_name}
          name="first_name"
          label="First name"
        />
        <TextField
          register={register}
          options={{ required: 'This field is required' }}
          error={errors.last_name}
          name="last_name"
          label="Last name"
        />
      </FormFieldset>
      <FormFieldset>
        <legend>Email address</legend>
        <EmailField
          register={register}
          options={{ required: 'This field is required' }}
          error={errors.email}
          name="email"
          label="Email address"
          autoComplete="off"
          required
        />
      </FormFieldset>
      <FormFieldset>
        <legend>Change your password</legend>
        <TextField
          register={register}
          type="password"
          options={{
            minLength: {
              value: 8,
              message: 'Password must have at least 8 characters',
            },
          }}
          error={errors.new_password}
          name="new_password"
          label="New password"
          autoComplete="off"
        />
        <TextField
          register={register}
          type="password"
          error={errors.confirm_password}
          options={{ validate: passwordsMatch }}
          name="confirm_password"
          label="Confirm new password"
          autoComplete="off"
        />
      </FormFieldset>
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          {/*<button className="sb-btn-cancel sb-btn" onClick={resetForm}>Cancel</button>*/}
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving...' : 'Save changes'}
            {mutation.isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {mutation.isError && (
          <Message
            messageStyle="error"
            title="Error saving changes"
            message="Please review your form"
          />
        )}
        {mutation.isSuccess && (
          <Message messageStyle="success" title="Successfully saved" />
        )}
      </div>
    </form>
  );
};
